import { Slider, Typography, Grid, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, AppBar, IconButton, Toolbar, LinearProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from "react";
import {
    DataGrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import DrillDownTable from "./DrillDownTable";
import { AvailableJournal, DrillDownParams } from "./CommonInterfaces";
import { useAuth } from "../contexts/authContext";

const START_YEAR = 2014;
const END_YEAR = 2025;

interface JournalSummaryResults {
    results: JournalSummary[],
    metadata: SummaryMetadata
}

interface SummaryMetadata {
    startYear: number,
    endYear: number
}

interface JournalSummary {
    journalId: string,
    journal: string,
    publisher: string,
    retractions: number,
    numberOfHighRiskArticles: number,
    retractionsPer10kArticles: number,
    highRiskPer10kArticles: number,
    actions: string
}


function valuetext(value: number) {
    return `${value}`;
}


const marks = [
    {
        value: START_YEAR,
        label: '' + START_YEAR,
    },
    {
        value: 2020,
        label: '2020',
    },
    {
        value: END_YEAR,
        label: '' + END_YEAR,
    }
];

interface JournalSummaryTableProps {
    mode: string | undefined
}

function JournalSummaryTable({ mode }: JournalSummaryTableProps) {

    const [hasInitialData, setHasInitialData] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [retractionsPerJournal, setRetractionsPerJournal] = useState<any[]>()
    const [yearRange, setYearRange] = useState<number[]>([START_YEAR, END_YEAR]);
    const [shownStartYear, setShownStartYear] = useState<number>(START_YEAR);
    const [shownEndYear, setShownEndYear] = useState<number>(END_YEAR);
    const [showDrilldownDialog, setShowDrilldownDialog] = useState(false)
    const [drillDownParams, setDrillDownParams] = useState<DrillDownParams>({ journalIds: '', startYear: START_YEAR, endYear: END_YEAR, drillDownTitle: "", queryRetractions: true, scitilityRiskScoreLowerBoundary: 1000 })
    const startYearInUse = useRef(START_YEAR)
    const endYearInUse = useRef(END_YEAR)
    const [open, setOpen] = useState(false);


    const handleClose = () => {
        setOpen(false);
    };

    const handleAdvancedFeaturesAlertClose = () => {
        setOpen(false);
    };

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setYearRange(newValue as number[]);
    };


    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ marginTop: 2 }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarQuickFilter sx={{ paddingLeft: 5 }} />
                <GridToolbarExport
                    slotProps={{
                        tooltip: { title: 'Export data' },
                        button: { variant: 'outlined' },
                    }}
                />
            </GridToolbarContainer>
        );
    }

    function showRetractions(journalId: string, journal: string) {
        setDrillDownParams({
            journalIds: journalId.replaceAll("https://openalex.org/", ""),
            startYear: yearRange[0],
            endYear: yearRange[1],
            drillDownTitle: "Retractions for " + journal + " between " + yearRange[0] + " and " + yearRange[1],
            queryRetractions: true,
            scitilityRiskScoreLowerBoundary: 0
        })
        setShowDrilldownDialog(true)
        setOpen(true)
    }


    async function showHighRiskArticles(journalId: string, journal: string) {
        setDrillDownParams({
            journalIds: journalId.replaceAll("https://openalex.org/", ""),
            startYear: yearRange[0],
            endYear: yearRange[1],
            drillDownTitle: "High Risk articles for " + journal + " published between " + yearRange[0] + " and " + yearRange[1],
            queryRetractions: false,
            scitilityRiskScoreLowerBoundary: 1.5
        })
        setShowDrilldownDialog(true)
        setOpen(true)
    }

    const columns: GridColDef<(JournalSummary)>[] = [
        { field: 'journal', headerName: 'Journal', flex: 0.5 },
        { field: 'publisher', headerName: 'Publisher', flex: 0.5 },
        { field: 'totalAnalysed', headerName: '# Analysed', flex: 0.3 },
        {
            field: 'retractions', headerName: '# Retractions', flex: 0.3,
            renderCell: (e) => {
                const onClick = (event: any, journalId: string, journal: string) => {
                    event.stopPropagation();
                    showRetractions(journalId, journal);
                };
                return <Button onClick={(event) => onClick(event, e.row.journalId, e.row.journal)}>{e.value}</Button>;
            }
        },
        {
            field: 'numberOfHighRiskArticles', headerName: '# High Risk', flex: 0.3,
            renderCell: (e) => {
                const onClick = (event: any, journalId: string, journal: string) => {
                    event.stopPropagation();
                    showHighRiskArticles(journalId, journal)
                };
                return <Button onClick={(event) => onClick(event, e.row.journalId, e.row.journal)}>{e.value}</Button>;
            }
        },
        { field: 'retractionsPer10kArticles', headerName: 'Retractions per 10k', flex: 0.3 },
        { field: 'highRiskPer10kArticles', headerName: 'High Risk per 10k', flex: 0.3 }
    ];

    const { publisherId } = useAuth()

    useEffect(() => {

        async function fetchData() {
            setIsLoading(true)
            startYearInUse.current = yearRange[0]
            endYearInUse.current = yearRange[1]
            let authToken = await auth.currentUser?.getIdToken(true)

            const reqUrl = (mode === 'publisher' && typeof publisherId === 'string') ? (
                '/stats/summary?grouping=journal&minSize=1&startYear=' + yearRange[0]
                + '&endYear=' + yearRange[1]
                + '&publisherIds=' + "https://openalex.org/" + publisherId.toLowerCase()
                + '&cache=4.5.0'
            ) : (
                '/stats/summary?grouping=journal&minSize=1000&startYear=' + yearRange[0] + '&endYear=' + yearRange[1] + '&cache=4.5.0'
            )

            fetch(argosApiEntryPoint + reqUrl
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: JournalSummaryResults) => {
                    if (data.metadata.startYear === startYearInUse.current && data.metadata.endYear === endYearInUse.current) {
                        setHasInitialData(true)
                        setShownStartYear(data.metadata.startYear)
                        setShownEndYear(data.metadata.endYear)
                        setRetractionsPerJournal(data.results)
                        setIsLoading(false)
                    }
                })
        }
        fetchData()
    }, [yearRange]);

    /**const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: string,
    ) => {
        if (newMode === "by_publication_year") {
            setMode(byPublicationYearMode)
        } else {
            setMode(byRetractionYearMode)
        }
    };**/

    function getRowId(row: JournalSummary) {
        return row.publisher + "_" + row.journal;
    }

    return <Paper elevation={5} sx={{ marginBottom: 5, padding: 2, width: (mode === 'publisher') ? 'auto' : '90%' }}>
        <Grid container>
            <Grid item xs={12} padding={2}>
                {mode === 'publisher' &&
                    <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                        Journals Summary
                    </Typography>}
                {mode !== 'publisher' &&
                    <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                        Journals Summary (journals over 1k articles analysed within timeframe)
                    </Typography>
                }
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h6">
                    {"Selected timeframe: " + shownStartYear + " - " + shownEndYear}
                </Typography>
            </Grid>
            <Grid item xs={12} md={6} padding={2}></Grid>
            <Grid item xs={12} md={6} sx={{ paddingRight: 2, paddingTop: 3 }}>
                <Slider
                    aria-labelledby="track-inverted-range-slider"
                    getAriaValueText={valuetext}
                    defaultValue={[START_YEAR, END_YEAR]}
                    onChange={handleSliderChange}
                    min={START_YEAR}
                    valueLabelDisplay="auto"
                    max={END_YEAR}
                    marks={marks}
                />
                {isLoading &&
                    <LinearProgress />}
            </Grid>
            {hasInitialData && !isLoading &&
                <>
                    <Grid item xs={12}>
                        <DataGrid
                            rows={retractionsPerJournal}
                            columns={columns}
                            getRowId={getRowId}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            columnHeaderHeight={120}
                            pageSizeOptions={[10, 20, 50]}
                            slots={{
                                toolbar: CustomToolbar,
                            }}
                            sx={{ '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { display: 'none' } }}
                        />
                    </Grid>
                </>
            }
            <Grid item xs={12} marginTop={2} paddingRight={2} >
                <Typography fontFamily='Roboto' color='#003366' align="right" sx={{ fontStyle: 'italic' }}>
                    Powered by Scitility PBC
                </Typography>
            </Grid>
        </Grid>
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Journal details
                    </Typography>
                    <Button autoFocus color="inherit" onClick={handleClose}>
                        Close
                    </Button>
                </Toolbar>
            </AppBar>
            <DrillDownTable
                journalIds={drillDownParams?.journalIds}
                startYear={drillDownParams?.startYear}
                endYear={drillDownParams?.endYear}
                drillDownTitle={drillDownParams?.drillDownTitle}
                queryRetractions={drillDownParams.queryRetractions}
                scitilityRiskScoreLowerBoundary={drillDownParams.scitilityRiskScoreLowerBoundary}
            />
        </Dialog>
    </Paper>
}

export default JournalSummaryTable